export enum CopilotConsentParam {
  ADMIN_CONSENT = 'admin_consent',
  TENANT = 'tenant',
  STATE = 'state',
  ERROR = 'error',
  ERROR_DESCRIPTION = 'error_description',
}

export const COPILOT_CONSENT_GRANTED_VALUE = 'true';

export const COPILOT_CONSENT_ROUTE = 'copilot-consent-complete';

export const COPILOT_MSAL_REDIRECT_PATH = '/assets/msal-redirect.html';

export const MICROSOFT_LOGIN_BASE_URL = 'https://login.microsoftonline.com';

/**
 * AADSTS650051 is an intermittent Entra race: the tenant's service principal for our app is
 * mid-provisioning when the redirect fires, so the consent is rejected even though a retry
 * a moment later succeeds. Matched on either the code or the message text.
 */
export const COPILOT_CONSENT_SP_PRESENT_CODE = 'AADSTS650051';
export const COPILOT_CONSENT_SP_PRESENT_MARKER = 'service principal name is already present';

/**
 * The SP-present race clears with time, not with anything different in the request - retrying
 * immediately after a short pause succeeds far more often than not. One automatic attempt per
 * tenant per browser session (tracked in sessionStorage, since this is a same-tab redirect round
 * trip to Microsoft and back) - if that also fails, hand control back to the admin instead of
 * looping.
 */
export const COPILOT_CONSENT_AUTO_RETRY_DELAY_MS = 3000;
export const COPILOT_CONSENT_AUTO_RETRY_STORAGE_PREFIX = 'copilotConsentAutoRetried:';
export const COPILOT_CONSENT_AUTO_RETRIED_VALUE = 'true';

/**
 * A Directory (tenant) ID is always a GUID. Anything else sends the administrator to a Microsoft
 * error page, so the approval actions are gated on this rather than on the field merely being filled.
 */
export const COPILOT_TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const COPILOT_TENANT_ID_EXAMPLE = '301ef2e0-70b3-4015-9b4c-42d297a7570f';

export const COPILOT_TENANT_ID_FORMAT_ERROR = `This isn't a valid tenant ID - it should look like ${COPILOT_TENANT_ID_EXAMPLE}.`;

export const COPILOT_TENANT_ID_SAVE_BLOCKED_ERROR = `The changes were not saved. ${COPILOT_TENANT_ID_FORMAT_ERROR}`;

export function isCopilotTenantIdValid(tenantId: string | null | undefined): boolean {
  return COPILOT_TENANT_ID_PATTERN.test((tenantId ?? '').trim());
}

/**
 * Single source of the admin-consent URL so the settings screen and the return page can never
 * drift on redirect_uri or the tenant carried in `state`. Returns '' when anything required is missing.
 */
export function buildCopilotAdminConsentUrl(origin: string, tenantId: string, appId: string): string {
  const cleanOrigin = (origin ?? '').replace(/\/+$/, '');
  const cleanTenantId = (tenantId ?? '').trim();
  const cleanAppId = (appId ?? '').trim();
  if (!cleanOrigin || !cleanAppId || !isCopilotTenantIdValid(cleanTenantId)) {
    return '';
  }
  const params = new URLSearchParams({
    client_id: cleanAppId,
    redirect_uri: `${cleanOrigin}/${COPILOT_CONSENT_ROUTE}`,
    state: cleanTenantId,
  });
  return `${MICROSOFT_LOGIN_BASE_URL}/${encodeURIComponent(cleanTenantId)}/adminconsent?${params.toString()}`;
}
